import { BaseApiClient } from './base-client'
import { API_ROUTES } from '@super/shared-types'
import type { WorkOSUser, ApiResponse } from '@super/shared-types'

/**
 * Session info response
 */
export interface SessionResponse {
  authenticated: boolean
  user?: WorkOSUser
  isSuperAdmin?: boolean
  reason?: string
}

/**
 * Widget token response
 */
export interface WidgetTokenResponse {
  success: boolean
  token?: string
  error?: string
}

/**
 * Authentication API Client
 * Handles authentication-related API calls
 */
class AuthClient extends BaseApiClient {
  /**
   * Get current session info
   */
  async getSession(): Promise<SessionResponse> {
    return this.request<SessionResponse>(API_ROUTES.AUTH.SESSION, {
      method: 'GET',
      credentials: 'include', // Include cookies
    })
  }

  /**
   * Redirect to the WorkOS AuthKit login/signup screen.
   * Pass `mode: 'signup'` to land on the sign-up screen (self-serve entry).
   */
  signIn(opts?: { state?: string; mode?: 'signin' | 'signup' }): void {
    const params = new URLSearchParams()
    if (opts?.state) params.set('state', opts.state)
    if (opts?.mode) params.set('mode', opts.mode)
    const query = params.toString() ? `?${params.toString()}` : ''
    window.location.href = `${this.baseUrl}${API_ROUTES.AUTH.LOGIN}${query}`
  }

  /**
   * Sign out by redirecting to logout endpoint
   */
  signOut(): void {
    window.location.href = `${this.baseUrl}${API_ROUTES.AUTH.LOGOUT}`
  }

  /**
   * Generate widget token for WorkOS widgets
   */
  async generateWidgetToken(scopes?: string[]): Promise<WidgetTokenResponse> {
    const response = await this.request<ApiResponse<WidgetTokenResponse>>(
      API_ROUTES.AUTH.WIDGET_TOKEN,
      {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ scopes }),
      },
    )
    return response.data!
  }

  /**
   * Switch to a different organization
   */
  async switchOrganization(organizationId: string): Promise<{
    success: boolean
    redirectUrl?: string
  }> {
    const response = await this.request<
      ApiResponse<{ success: boolean; redirectUrl?: string }>
    >(API_ROUTES.AUTH.SWITCH_ORGANIZATION, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ organizationId }),
    })
    return response.data!
  }
}

// Export singleton instance
export const authClient = new AuthClient()
